// To be used by both, server and by client components!

import { Locales } from "./link-utils/constants.cjs"
import urlScheme from "./link-utils/url-scheme.js"
import { logger } from "./logger"

export function getUbLocaleFromHostName(requestedHostName: string): string {
  for (const domain in urlScheme.defaultUbLocaleByDomain) {
    if (removePort(requestedHostName).endsWith(domain)) {
      return urlScheme.defaultUbLocaleByDomain[domain] //ubLocale
    }
  }
  return Locales.defaultUbLocaleForDefaultSbLocale
}

function removePort(hostname: string): string {
  return hostname.split(":")[0]
}

function getSbLocaleFromParams(ubLocaleFromUri: string, ubLocaleFromHostName: string, sbLocaleFromUri: string): string {
  // precedence:
  // lang from SB in URI
  // > language from URI in CH or BE (see next.config)
  // > Hostname
  // > (ignored: locale in (original) URI)

  // ubLocaleFromUri must be checked before sbLocaleFromUri because of the risk of confusion:
  //     "/fr/chf" or "/fr/eur" also matches sbLocale (-> "fr"), but desired result is "fr-ch" or "fr-be" in this case.
  if ((ubLocaleFromHostName === "ch-de" || ubLocaleFromHostName === "be-nl") && ubLocaleFromUri) {
    return getSbLocaleFromUbLocale(ubLocaleFromUri)
  }

  if (sbLocaleFromUri) {
    return sbLocaleFromUri
  }

  if (ubLocaleFromHostName == Locales.defaultUbLocaleForDefaultSbLocale) {
    return Locales.defaultSbLocale
  }
  return getSbLocaleFromUbLocale(ubLocaleFromHostName)
}

export function getSbLocaleFromUbLocale(ubLocale: string): string {
  const locale = getSbLocaleFromUbLocaleWithoutFallback(ubLocale)
  return locale == Locales.shortLocaleForDefaultSbLocale ? Locales.defaultSbLocale : locale
}

function getSbLocaleFromUbLocaleWithoutFallback(ubLocale: string): string {
  if (!ubLocale) {
    return ubLocale
  }
  const parts = ubLocale.toLowerCase().split("-")
  if (parts.length <= 1) {
    return ubLocale
  }

  // "xy-xy" -> "xy"
  if (parts[0] == parts[1]) {
    return parts[0]
  }

  return parts[1] + "-" + parts[0]
}

export function getSbLocaleFromIsoLocale(isoLocale: string): string {
  const sbLocale = Locales.isoLocaleToSbLocale[isoLocale]
  if (!sbLocale) {
    return ""
  } else {
    return sbLocale
  }
}

export function getSbLocaleFromUri(uri: string): string {
  // match URI in the Storyblok generated form /de/...
  const sbLocaleRegex = new RegExp(
    `^\/(${Object.keys(Locales.sbLocaleToIsoLocale)
      .filter((key) => key)
      .join("|")})([^a-z]|$)`
  )
  const match = uri.match(sbLocaleRegex)
  if (!match) {
    return ""
  }
  const sbLocaleFromUri = match[1]
  // logger.debug(`getSbLocaleFromUri(${uri}) returns ${sbLocaleFromUri}.`)
  return sbLocaleFromUri
}

export function getUbLocaleFromSbLocale(sbLocale: string | undefined) {
  if (!sbLocale) {
    return Locales.sbLocaleToUbLocale[Locales.defaultSbLocale]
  }

  const ubLocale = Locales.sbLocaleToUbLocale[sbLocale]
  if (!ubLocale) {
    logger.warn(`No ubLocale found for sbLocale: ${sbLocale}`)
    return ""
  }

  return ubLocale
}

export function getUbLocaleFromUri(uri) {
  // const ubLocaleRegex: RegExp = /^\/([a-z]{2}(\/[a-z]{2,3})?)([^a-z]|$)/i // e.g. /fr/CHF or /fr/EUR
  // match URI in Udobär form where only /fr/CHF or /fr/EUR is relevant and has to be considered
  if (uri.toLowerCase().startsWith("/fr/chf/") || uri.toLowerCase() === "/fr/chf") {
    return "ch-fr"
  }
  if (uri.toLowerCase().startsWith("/fr/eur/") || uri.toLowerCase() === "/fr/eur") {
    return "be-fr"
  }
  return ""
}

const DEBUG_LOCALE_SB_LOCALE_FROM_PARAMS = process.env.DEBUG_LOCALE_SB_LOCALE_FROM_PARAMS === "true"

export function getSbLocaleFromHostAndUri(hostname: string, uri: string) {
  const sbLocaleFromUri = getSbLocaleFromUri(uri)
  const ubLocaleFromUri = getUbLocaleFromUri(uri)

  const ubLocaleFromHostName = getUbLocaleFromHostName(hostname)
  const sbLocale = getSbLocaleFromParams(ubLocaleFromUri, ubLocaleFromHostName, sbLocaleFromUri)
  DEBUG_LOCALE_SB_LOCALE_FROM_PARAMS &&
    logger.debug(
      `getSbLocaleFromParams: hostname=${hostname}, uri=${uri} ubLocaleFromUri=${ubLocaleFromUri}, ubLocaleFromHostName=${ubLocaleFromHostName}, sbLocaleFromUri=${sbLocaleFromUri} -> sbLocale=${sbLocale}.`
    )
  return sbLocale
}

// returns: [ "", "de-at", ...]
export function getAllSbLocales(): string[] {
  return Object.keys(Locales.sbLocaleToIsoLocale)
}
/*

export function getAllSbLocalesWithFilledDefault(): string[] {
  const sbLocales = getAllSbLocales().map((sbLocale) =>
    sbLocale === Locales.defaultSbLocale ? Locales.shortLocaleForDefaultSbLocale : sbLocale
  )
  logger.debug(`getSbLocaleWithFilledDefault: ${sbLocales}`)
  return sbLocales
}
*/

// Extract a slug from a URL or path by stripping ub-locale (/fr/CHF, /fr/EUR)
// and sb-locale (/de-at, /fr-ch, ...). Returns slug without leading slash.
export function getLocalePathPrefixFromSbLocale(sbLocale?: string): string {
  switch (sbLocale) {
    case "fr-ch":
      return "/fr/CHF"
    case "fr-be":
      return "/fr/EUR"
    default:
      return ""
  }
}

// New normalized helper: removes any existing locale prefixes and re-applies
// the correct non-default prefix (e.g., /fr/CHF or /fr/EUR) based on sbLocale.
// Returns a root-relative path (always starts with "/").
export function removeDefaultLocalesFromUrl(input: string, sbLocale?: string): string {
  if (!input) return "/"

  let path = input
  if (/^https?:\/\//i.test(input)) {
    try {
      path = new URL(input).pathname
    } catch (_) {}
  }
  if (!path.startsWith("/")) path = "/" + path

  // Strip UB locale prefix first (CH/BE variants)
  const ub = getUbLocaleFromUri(path)
  if (ub === "ch-fr") path = path.substring("/fr/CHF".length) || "/"
  if (ub === "be-fr") path = path.substring("/fr/EUR".length) || "/"

  // Strip Storyblok sb-locale prefix next
  const sb = getSbLocaleFromUri(path)
  if (sb) path = path.substring(sb.length + 1) || "/" // +1 for leading slash

  // Resolve sb-locale if not provided (safe on client)
  let effectiveSbLocale = sbLocale
  if (!effectiveSbLocale && typeof window !== "undefined") {
    effectiveSbLocale = getSbLocaleFromHostAndUri(window.location.hostname, window.location.pathname)
  }

  const prefix = getLocalePathPrefixFromSbLocale(effectiveSbLocale)
  const slug = path.startsWith("/") ? path.slice(1) : path
  const joined = (prefix ? prefix + "/" : "/") + slug
  return joined.replace(/\/+/, "/")
}

// Backward-compat: original behavior returned slug without leading slash.
// Keep as alias for now in case of lingering imports.
export function extractSlugWithoutLocale(input: string): string {
  const normalized = removeDefaultLocalesFromUrl(input)
  // Strip any leading locale prefix we may have added and return just the slug
  const ub = getUbLocaleFromUri(normalized)
  let path = normalized
  if (ub === "ch-fr") path = normalized.substring("/fr/CHF".length) || "/"
  if (ub === "be-fr") path = normalized.substring("/fr/EUR".length) || "/"
  const sb = getSbLocaleFromUri(path)
  if (sb) path = path.substring(sb.length + 1) || "/"
  return path.startsWith("/") ? path.slice(1) : path
}
